import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, TrendingDown, Package } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { useTenant } from "@/contexts/TenantContext";
import { api, getActiveBranchName } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import { DateRangeFilter, presetRange } from "@/components/reports/DateRangeFilter";
import { BranchFilter } from "@/components/reports/BranchFilter";
import { KpiCard, formatMxn, formatInt } from "@/components/reports/KpiCard";
import { ExportMenu } from "@/components/reports/ExportMenu";
import { ShareExportPanel } from "@/components/reports/ShareExportPanel";

export default function StockAlerts() {
  const { isSuperAdmin, activeTenantId } = useTenant();
  const [range, setRange] = useState(() => ({ preset: "month", ...presetRange("month") }));
  const [branchId, setBranchId] = useState("");
  const [thresholdDays, setThresholdDays] = useState(60);
  const [tab, setTab] = useState("low");

  const [lowData, setLowData] = useState(null);
  const [slowData, setSlowData] = useState(null);
  const [consData, setConsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consGroupBy, setConsGroupBy] = useState("branch");

  const params = useMemo(() => {
    const p = {};
    if (branchId) p.branch_id = branchId;
    if (isSuperAdmin && activeTenantId) p.tenant_id = activeTenantId;
    return p;
  }, [branchId, isSuperAdmin, activeTenantId]);

  const paramsWithRange = useMemo(() => {
    const p = { ...params };
    if (range.date_from) p.date_from = range.date_from;
    if (range.date_to) p.date_to = range.date_to;
    return p;
  }, [params, range]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [l, s, c] = await Promise.all([
          api.get("/inventory/low-stock", { params }),
          api.get("/inventory/slow-movers", { params: { ...paramsWithRange, threshold_days: thresholdDays } }),
          api.get("/inventory/consumption", { params: { ...paramsWithRange, group_by: consGroupBy } }),
        ]);
        if (!cancelled) {
          setLowData(l.data);
          setSlowData(s.data);
          setConsData(c.data);
        }
      } catch (_) {
        if (!cancelled) {
          setLowData(null); setSlowData(null); setConsData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params, paramsWithRange, thresholdDays, consGroupBy]);

  return (
    <div className="space-y-6" data-testid="page-stock-alerts">
      <PageHeader
        title="Alertas de inventario"
        description="Refacciones bajo mínimo, slow movers y consumo por sucursal."
        actions={
          <div className="flex gap-2 flex-wrap">
            {tab === "low" && (
              <ShareExportPanel
                reportKey="low_stock"
                reportName="Alertas de bajo stock"
                endpoint="/inventory/low-stock"
                params={params}
                exportPermission="analytics:export"
                filename="stock-bajo"
                branchId={branchId || null}
                filters={{
                  branch_name: getActiveBranchName(),
                }}
                stats={{
                  items_count: lowData?.count,
                  below_min: lowData?.count,
                  need_reorder: lowData?.count,
                  row_count: lowData?.items?.length,
                }}
                testIdPrefix="low-stock-share"
              />
            )}
            {tab === "slow" && (
              <ShareExportPanel
                reportKey="slow_movement"
                reportName="Movimientos lentos"
                endpoint="/inventory/slow-movers"
                params={{ ...paramsWithRange, threshold_days: thresholdDays }}
                exportPermission="analytics:export"
                filename="slow-movers"
                branchId={branchId || null}
                filters={{
                  branch_name: getActiveBranchName(),
                  threshold_days: thresholdDays,
                }}
                stats={{
                  items_count: slowData?.count,
                  stuck_value: slowData?.totals?.stuck_value,
                  row_count: slowData?.items?.length,
                }}
                testIdPrefix="slow-movers-share"
              />
            )}
            {tab === "consumption" && (
              <ExportMenu
                endpoint="/inventory/consumption"
                params={{ ...paramsWithRange, group_by: consGroupBy }}
                permission="analytics:export"
                filename="consumo"
                testId="export-consumption"
              />
            )}
          </div>
        }
      />

      <Card className="border-border">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between">
          <DateRangeFilter value={range} onChange={setRange} />
          <BranchFilter value={branchId} onChange={setBranchId} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="stock-kpis">
        <KpiCard
          label="Refacciones bajo mínimo"
          value={formatInt(lowData?.count)}
          icon={AlertTriangle}
          testId="kpi-low"
          loading={loading}
        />
        <KpiCard
          label="Slow movers"
          value={formatInt(slowData?.count)}
          hint={`> ${thresholdDays} días sin salida`}
          icon={TrendingDown}
          testId="kpi-slow"
          loading={loading}
        />
        <KpiCard
          label="Capital muerto (slow)"
          value={formatMxn(slowData?.total_dead_capital)}
          hint="Valor de inventario sin rotación"
          icon={Package}
          testId="kpi-dead"
          loading={loading}
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="stock-tabs">
          <TabsTrigger value="low" data-testid="tab-low">Bajo mínimo</TabsTrigger>
          <TabsTrigger value="slow" data-testid="tab-slow">Slow movers</TabsTrigger>
          <TabsTrigger value="consumption" data-testid="tab-consumption">Consumo</TabsTrigger>
        </TabsList>

        <TabsContent value="low">
          <Card className="border-border" data-testid="low-stock-table">
            <CardHeader className="border-b border-border">
              <CardTitle className="tracking-tight">Refacciones por debajo de stock mínimo</CardTitle>
              <CardDescription>Compra sugerida basada en el gap × costo unitario.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)}</div>
              ) : lowData?.items?.length ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU</TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Categoría</TableHead>
                        <TableHead className="text-right">Stock</TableHead>
                        <TableHead className="text-right">Mínimo</TableHead>
                        <TableHead className="text-right">Gap</TableHead>
                        <TableHead className="text-right">Compra sugerida</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lowData.items.map((it) => (
                        <TableRow key={it.part_id} data-testid={`low-row-${it.part_id}`}>
                          <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                          <TableCell>{it.name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{it.category || "—"}</TableCell>
                          <TableCell className="text-right">{it.current_stock}</TableCell>
                          <TableCell className="text-right">{it.min_stock}</TableCell>
                          <TableCell className="text-right font-semibold text-amber-700">{it.gap}</TableCell>
                          <TableCell className="text-right">{formatMxn(it.suggested_purchase_value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-6 text-sm text-muted-foreground">Sin refacciones bajo mínimo.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="slow">
          <Card className="border-border" data-testid="slow-movers-table">
            <CardHeader className="border-b border-border flex flex-row items-center justify-between">
              <div>
                <CardTitle className="tracking-tight">Slow movers</CardTitle>
                <CardDescription>Refacciones sin salida en el umbral definido.</CardDescription>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Umbral (días)
                </div>
                <Select value={String(thresholdDays)} onValueChange={(v) => setThresholdDays(Number(v))}>
                  <SelectTrigger className="h-9 w-28" data-testid="slow-threshold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[30, 60, 90, 120, 180, 365].map((d) => (
                      <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)}</div>
              ) : slowData?.items?.length ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU</TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead className="text-right">Stock</TableHead>
                        <TableHead className="text-right">Últ. salida (días)</TableHead>
                        <TableHead className="text-right">Costo unitario</TableHead>
                        <TableHead className="text-right">Capital muerto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {slowData.items.map((it) => (
                        <TableRow key={it.part_id} data-testid={`slow-row-${it.part_id}`}>
                          <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                          <TableCell>{it.name}</TableCell>
                          <TableCell className="text-right">{it.current_stock}</TableCell>
                          <TableCell className="text-right text-red-700">
                            {it.last_out_days === null ? "Nunca" : it.last_out_days}
                          </TableCell>
                          <TableCell className="text-right">{formatMxn(it.unit_cost || 0)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatMxn(it.dead_capital)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-6 text-sm text-muted-foreground">Sin slow movers en el período.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="consumption">
          <Card className="border-border" data-testid="consumption-table">
            <CardHeader className="border-b border-border flex flex-row items-center justify-between">
              <div>
                <CardTitle className="tracking-tight">Consumo del período</CardTitle>
                <CardDescription>Salidas de inventario por sucursal o por refacción.</CardDescription>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Agrupar
                </div>
                <Select value={consGroupBy} onValueChange={setConsGroupBy}>
                  <SelectTrigger className="h-9 w-36" data-testid="cons-groupby">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="branch">Por sucursal</SelectItem>
                    <SelectItem value="part">Por refacción</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)}</div>
              ) : consData?.items?.length ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{consGroupBy === "branch" ? "Sucursal" : "Refacción"}</TableHead>
                        {consGroupBy === "part" && <TableHead>SKU</TableHead>}
                        <TableHead className="text-right">Salidas</TableHead>
                        <TableHead className="text-right">Costo total</TableHead>
                        <TableHead className="text-right">Movimientos</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {consData.items.map((it, idx) => (
                        <TableRow key={idx} data-testid={`cons-row-${idx}`}>
                          <TableCell className="font-medium">{it.label}</TableCell>
                          {consGroupBy === "part" && (
                            <TableCell className="font-mono text-xs">{it.sku || "—"}</TableCell>
                          )}
                          <TableCell className="text-right">{it.exits}</TableCell>
                          <TableCell className="text-right">{formatMxn(it.cost)}</TableCell>
                          <TableCell className="text-right">{it.movements}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-6 text-sm text-muted-foreground">Sin consumo registrado.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
